import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CURRENCIES } from 'config';

// Currency ID mapping (maps currency codes to backend currency IDs)
// Default: GBP = 1 (Pound Sterling)
const CURRENCY_ID_MAP: Record<string, number> = {
  'GBP': 1,  // Pound Sterling (default)
  'EUR': 2,  // Euro
  // 'USD': 3,  // US Dollar
  // 'CHF': 4,  // Swiss Franc
  // 'CAD': 5,  // Canadian Dollar
  // 'JPY': 6,  // Japanese Yen
};

interface CurrencyContextType {
  selectedCurrency: typeof CURRENCIES[number];
  currencyId: number;
  setCurrency: (currency: typeof CURRENCIES[number]) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [selectedCurrency, setSelectedCurrencyState] = useState<typeof CURRENCIES[number]>(CURRENCIES[0]);

  // Load currency from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selected_currency');
    if (savedCurrency) {
      try {
        const currency = JSON.parse(savedCurrency);
        const foundCurrency = CURRENCIES.find(c => c.code === currency.code);
        if (foundCurrency) {
          setSelectedCurrencyState(foundCurrency);
        }
      } catch (error) {
        console.error('Error loading currency from storage:', error);
      }
    }
  }, []);

  // Save currency to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selected_currency', JSON.stringify(selectedCurrency));
  }, [selectedCurrency]);

  const setCurrency = (currency: typeof CURRENCIES[number]) => {
    setSelectedCurrencyState(currency);
  };

  const currencyId = CURRENCY_ID_MAP[selectedCurrency.code] || 1; // Default to GBP (ID: 1)

  const value: CurrencyContextType = {
    selectedCurrency,
    currencyId,
    setCurrency,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

